<template>
  <div
    :class="bem({
      layout: block.stylingLayout,
      hasImage: hasImage,
      textStyle: block.stylingTextStyle,
      backgroundColours: block.stylingBackgroundColours.length
    })"
  >
    <div
      v-if="hasImage"
      :class="bem('image')"
    >
      <DatoCmsImage
        :data="block.image.responsiveImage"
        object-fit="cover"
        :object-position="`${block.image.focalPoint.x * 100}% ${
          block.image.focalPoint.y * 100
        }%`"
        layout="fill"
      />
    </div>
    <div
      :class="bem('form', {
        loading,
        success
      })"
    >
      <TitleBlock
        v-if="block.title?.text"
        :title="block.title"
        :class="bem('title')"
      />
      <StructuredText
        v-if="block.body"
        :class="bem('body')"
        :data="block.body"
      />
      <form
        v-if="!success"
        :class="bem('form-inner')"
        @submit.prevent="submitNewsletter"
      >
        <div
          :class="bem('form-inner-inputs')"
        >
          <VisionTextInput
            id="subscribe-email"
            v-model:value="email"
            :model-value="email"
            type="email"
            :shape="props.block.stylingButtonStyle"
            :placeholder="block.formEmailPlaceholder"
            :class="bem('form-input', {
              colour: block.stylingInputStyle
            })"
            :disabled="loading || success"
            :error="message"
            name="subscribeEmail"
            @keyup.enter="submitNewsletter"
          />
          <VisionButton
            :class="bem('button')"
            :background-colour="buttonStyles.bgColour"
            :text-colour="buttonStyles.textColour"
            :square="buttonStyles.style"
            :disabled="loading || success"
          >
            <template #content>
              <span
                :class="bem('buton-inner', {
                  loading
                })"
              >
                {{ block.formCtaText }}
              </span>
              <VisionLoadingSpinner
                :loading="loading"
                :spinner-colour="buttonStyles.textColour === 'black'
                  ? 'blue' : buttonStyles.textColour"
                :class="bem('button-loading')"
                size="small"
              />
            </template>
          </VisionButton>
        </div>
        <VisionCheckbox
          v-if="block.formOptIn"
          id="optIn"
          v-model:model-value="optIn"
          input-value=""
          name="newsletterOptIn"
          :label-text="block.formOptInText"
          :class="bem('opt-in', {
            optInError
          })"
        />
      </form>
      <div
        v-else
        :class="bem('success')"
      >
        <GenericStructuredText
          :content="successStructuredText"
        />
      </div>
      <BaseCopy
        key="privacyNote"
        size="body-sm"
        :class="bem('privacy-notice')"
      >
        You can unsubscribe at any time, we treat your information with
        respect.
        <VisionLink
          :line-link="true"
          :is-button="true"
          :theme="block.stylingTextStyle === 'light' ? 'white' : 'default'"
          display="inline-block"
          size="inherit"
          @click="openPrivacyPolicy"
        >
          Privacy Policy.
        </VisionLink>
      </BaseCopy>
    </div>
  </div>
</template>

<script setup>
import { Image as DatoCmsImage, StructuredText } from 'vue-datocms'
const { $sentry, $track } = useNuxtApp()
const bem = useBEM('EmailSignupRow')
const sidebarStore = useSidebarStore();
const userStore = useUserStore();

const props = defineProps({
  block: {
    type: Object,
    required: true
  },
  rowPosition: {
    type: Number,
    default: 0,
  },
})

const email = ref(null);
const message = ref('');
const loading = ref(false);
const success = ref(false);
const optIn = ref(false);
const optInError = ref(false)

const gradientAngle = `${props.block.stylingBackgroundGradientAngle}deg`
const hasImage = props.block.image?.responsiveImage

const buttonStyles = computed(() => {
  const bgColour = props.block.stylingButton;
  return {
    textColour: (bgColour === 'gradient' || bgColour === 'black' ? 'white' : 'black'),
    bgColour: bgColour,
    style: props.block.stylingButtonStyle === 'square'
  }
})

// Format structured text for GenericStructuredText
const successStructuredText = computed(() => {
  return {
    copy: props.block.formSuccess,
    textColour: props.block.stylingTextStyle,
    textAlign: 'left',
    mobileTextAlign: 'left'
  }
})



function openPrivacyPolicy() {
  sidebarStore.setActiveSidebar('privacy-policy')
}

function backgroundColour(index) {
  return props.block.stylingBackgroundColours[index]?.colour.hex || '#ffffff'
}

watch(email, () => {
  // Reset message to an empty string whenever email changes to remove input error
  message.value = '';
  optInError.value = false
});

const submitNewsletter = async () => {
  if (!email.value) {
    message.value = 'Email Address Required'
    
    if (!optIn.value && props.block.formOptIn) {
      optInError.value = true
    }

    return
  }

  if (!optIn.value && props.block.formOptIn) {
    optInError.value = true
    return
  }

  loading.value = true;
  optInError.value = false

  try {
    const response = await $fetch(
      `/subscribe`,
      {
        method: 'POST',
        body: {
          email: email.value
        },
      }
    );

    if (response.subscribed) {
      success.value = true;
      userStore.setUserEmail(email.value);
      $track.updateUserData();
      $track.form.submitted({
        formId: props.block.trackingId || 'Email Signup',
        interactionElement: 'Signup Form',
        interactionCategory: 'Email Signup',
        interactionSection: props.block.trackingId || 'Email Signup',
        position: props.rowPosition + 1
      })
    } else if ('valid' in response && !response.valid) {
      this.$track.form.invalid({
        formId: props.block.trackingId || 'Email Signup',
        interactionElement: 'Signup Form',
        interactionCategory: 'Email Signup',
        interactionSection: props.block.trackingId || 'Email Signup',
        position: props.rowPosition + 1,
        reason: response.reason
      })
      if (response.reason === 'already_subscribed') {
        message.value = 'That email address has already signed up.'
      } else {
        message.value = 'Please enter a valid email address and try again.'
      }
    } else {
      message.value = 'An error occurred, please try again.'
    }
  } catch (error) {
    if ('valid' in (error?.data?.data || {}) && !error.data.data.valid) {
      $track.form.invalid(error.data.data.reason);
      message.value = 'Please enter a valid email address and try again.';
    } else {
      $sentry.captureException(error);
      message.value = 'An error occurred, please try again.';
    }
  }

  loading.value = false
};
</script>

<style lang="postcss">
.EmailSignupRow {
  @apply w-full mx-auto mb-20 md:p-10 bg-[v-bind(backgroundColour(0))] z-[2];

  /**
    MODIFIERS:

    --layout-full: Takes up full page widrth
    --layout-contain: Rounded edges boxed
    --text-style-light: White text
    --background-colours: Adds gradient when multiple colours in array
    --has-image: Has context image present
   */

  &--layout-full {
    @apply w-full global-wrapper-reset;

    &:not(.EmailSignupRow--has-image) .EmailSignupRow__form {
      @apply max-w-screen-md mx-auto;
    }
  }

  &--layout-contain {
    @apply rounded-xl;

    &:not(.EmailSignupRow--has-image) {
      @apply max-w-screen-md mdMax:mx-[var(--outer-gutter)];
    }
  }

  &--text-style-light {
    @apply text-white;

    .EmailSignupRow__title {
      @apply text-white;
    }
  }

  &--background-colours-2 {
    background: linear-gradient(v-bind(gradientAngle), v-bind(backgroundColour(0)) 0%, v-bind(backgroundColour(1)) 100%);
  }

  &--background-colours-3 {
    background: linear-gradient(v-bind(gradientAngle), v-bind(backgroundColour(0)) 0%, v-bind(backgroundColour(1)) 50%, v-bind(backgroundColour(2)) 100%);
  }

  &--has-image {
    @apply grid overflow-hidden
    items-center min-h-[30rem] p-0;

    @screen md {
      @apply grid-cols-2;
    }

    &:not(.EmailSignupRow--layout-full) {
      @apply max-w-screen-xl xlMax:mx-[var(--outer-gutter)];
    }
  }
}

.EmailSignupRow__title {
  @apply w-fit;
}

.EmailSignupRow__body {
  @apply mt-4;
}

.EmailSignupRow__form {
  @apply p-5 py-10 md:p-10;
}

.EmailSignupRow__form-inner-inputs {
  @apply flex flex-col md:flex-row items-start md:items-stretch
  w-full gap-1 gap-y-2 mt-5;
}

.EmailSignupRow__form-input {
  @apply w-full md:w-fit flex-1;

  &--colour-dark .VisionTextInput__input {
    @apply bg-off-black text-white;
  }

  &--colour-light .VisionTextInput__input {
    @apply bg-white;
  }
}

.EmailSignupRow__image {
  @apply w-full relative aspect-[5/4] md:aspect-square min-h-full;
}

.EmailSignupRow__button {
  @apply relative max-h-[57px];

  &--style-square {
    @apply max-h-[40px]
  }
}

.EmailSignupRow__privacy-notice {
  @apply mt-3;
}

.EmailSignupRow__success {
  @apply mt-3;

  .GenericStructuredText__content {
    @apply gap-y-1;
  }
}

.EmailSignupRow__opt-in {
  @apply mt-4;

  &--opt-in-error {
    @apply text-red;

    .VisionCheckbox__checkmark {
      @apply border-red;
    }
  }
}
</style>
